// UserProfile component for displaying user info and sign out option
import { useAppSelector } from '@/app/hooks';
import { selectSessionUser } from '@/features/session/store/session.selector';
import { Button } from '@yaradigitallabs/ahua-react';

// Props for UserProfile
// signOut: callback for logging out
interface UserProfileProps {
  signOut: () => void;
}

// UserProfile displays user's avatar, name, email, and logout button
export const UserProfile = ({ signOut }: UserProfileProps) => {
  const user = useAppSelector(selectSessionUser);

  // Returns user's initials for avatar
  const getUserInitials = () => {
    if (!user) return 'GU';
    const firstName = user.givenName || '';
    const lastName = user.familyName || '';
    const initials =
      `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    return initials.length > 0 ? initials : 'GU';
  };

  // Returns user's full name or fallback
  const getUserFullName = () => {
    if (!user) return 'Guest User';
    return (
      `${user.givenName || ''} ${user.familyName || ''}`.trim() ||
      user.email ||
      'User'
    );
  };

  // Handles logout button click
  const handleLogout = () => {
    signOut();
  };

  return (
    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
      {/* User Info Section */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          {/* User Avatar with initials */}
          <div className="shrink-0">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {getUserInitials()}
            </div>
          </div>
          {/* User Details: name and email */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {getUserFullName()}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email || 'No email available'}
            </p>
          </div>
        </div>
      </div>

      {/* Logout button */}
      <Button
        css={{ width: '100%', justifyContent: 'flex-start' }}
        variant="ghost"
        colorConcept="destructive"
        onClick={handleLogout}
        iconLeading="LogOut"
        size="s"
      >
        Log Out
      </Button>
    </div>
  );
};
